import { createContext, useContext, useReducer, useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { setupAxiosInterceptors } from "services/interceptor";

const MaterialUI = createContext();

// Authentication Context
export const AuthContext = createContext({
  isAuthenticated: false,
  role: null,
  permissions: [],
  requiredRoles: {},
  hasPermission: () => false,
  login: () => { },
  register: () => { },
  logout: () => { },
  refreshPermissions: () => { },
});

const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("token"));
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [requiredRoles, setRequiredRoles] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("token");

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("fbSLAT");
    localStorage.removeItem("activeWorkspaceId");
    setIsAuthenticated(false);
    navigate("/auth/login");
  }, [navigate]);

  // ✅ Setup interceptors IMMEDIATELY on initialization (Sync)
  // This prevents race conditions where children (like ProtectedRoute)
  // fire requests before the useEffect mount trigger.
  setupAxiosInterceptors(() => {
    logout();
  });

  const refreshPermissions = async () => {
    const activeWorkspaceId = localStorage.getItem("activeWorkspaceId");
    if (!activeWorkspaceId) {
      console.warn("⚠️ RBAC: Skipping permission refresh - No active workspace ID found in storage.");
      return;
    }

    try {
      // Import and call WorkspaceService (Dynamic import for now to avoid circular if any)
      const { fetchWorkspaceEntitlements } = (await import("services/workspace-service")).default;
      const data = await fetchWorkspaceEntitlements();
      if (data) {
        setRole(data.role);
        setPermissions(data.permissions || []);
        setRequiredRoles(data.requiredRoles || {});
        console.log(`✅ RBAC: Loaded role [${data.role}] with [${data.permissions?.length}] permissions.`);
      }
    } catch (err) {
      console.error("❌ RBAC: Failed to fetch permissions:", err);
    }
  };

  useEffect(() => {
    if (!token) return;

    setIsAuthenticated(true);
    refreshPermissions();
  }, [token]);

  // 🔥 Listen for workspace switch event
  useEffect(() => {
    const handleRefresh = () => {
      console.log("🔄 RBAC: Detected workspace switch, refreshing permissions...");
      refreshPermissions();
    };
    window.addEventListener("profile-updated", handleRefresh);
    return () => window.removeEventListener("profile-updated", handleRefresh);
  }, []);


  const hasPermission = useCallback((perm) => {
    // Demo users are workspace owners — always pass RBAC.
    // Their feature restrictions are handled by LiveDemoRestriction separately.
    if (sessionStorage.getItem('demo_role_cache') === 'demo_viewer') return true;
    return permissions.includes(perm);
  }, [permissions]);

  const login = useCallback((token, workspaceId = null, redirectPath = "/dashboard") => {
    // console.log("Contorl is here now.");
    localStorage.setItem("token", token);
    if (workspaceId) {
      localStorage.setItem("activeWorkspaceId", workspaceId);
    }
    setIsAuthenticated(true);
    navigate(redirectPath);
    // console.log("Contorl is here now after nav to dashboard.");
  }, [navigate]);


  const authValue = useMemo(() => ({
    isAuthenticated,
    role,
    permissions,
    requiredRoles,
    hasPermission,
    login,
    logout,
    refreshPermissions
  }), [isAuthenticated, role, permissions, requiredRoles, hasPermission, login, logout, refreshPermissions]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Search and Platform Context
export const PlatformScoutContext = createContext({
  searchResults: [],
  setSearchResults: () => { },
  currentPlatform: "facebook",
  setCurrentPlatform: () => { },
});

const PlatformScoutProvider = ({ children }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [currentPlatform, setCurrentPlatform] = useState("facebook");

  return (
    <PlatformScoutContext.Provider value={{ searchResults, setSearchResults, currentPlatform, setCurrentPlatform }}>
      {children}
    </PlatformScoutContext.Provider>
  );
};

// Setting custom name for the context which is visible on React Dev Tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };
    case "WHITE_SIDENAV":
      return { ...state, whiteSidenav: action.value };
    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };
    case "DIRECTION":
      return { ...state, direction: action.value };
    case "LAYOUT":
      return { ...state, layout: action.value };
    case "DARKMODE":
      return { ...state, darkMode: action.value };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error("useMaterialUIController should be used inside the MaterialUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

// RBAC Helpers
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth should be used inside the AuthContextProvider.");
  }
  return context;
}

function usePermissions() {
  const { role, permissions, requiredRoles, hasPermission, refreshPermissions } = useAuth();
  return { role, permissions, requiredRoles, hasPermission, refreshPermissions };
}

function PermissionGuard({ permission, children, fallback = null }) {
  const { hasPermission } = usePermissions();
  if (!hasPermission(permission)) return fallback;
  return children;
}

export {
  AuthContextProvider,
  PlatformScoutProvider,
  MaterialUIControllerProvider,
  useMaterialUIController,
  useAuth,
  usePermissions,
  PermissionGuard,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
